import { Suspense, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Box } from "@mui/material";
import Sidebar from "./components/common/navigation/Sidebar";
import { useDevice } from "./providers/DeviceProvider";
import { Routes, Route, useNavigate } from "react-router";
import DispatchPage from "./components/pages/dispatch/DispatchPage";
import NotFoundPage from "./components/pages/error/NotFoundPage";
import MissionPage from "./components/pages/mission/MissionPage";
import CauseRegister from "./components/pages/registers/CauseRegister";
import ServiceVehicleRegister from "./components/pages/registers/ServiceVehicleRegister";
import MissionTagRegister from "./components/pages/registers/MissionTagRegister";
import ContactRegister from "./components/pages/contacts/ContactRegister";
import ContactPage from "./components/pages/contacts/ContactPage";
import ProductRegister from "./components/pages/registers/ProductRegister";
import AgreementRegister from "./components/pages/agreements/AgreementRegister";
import AgreementPage from "./components/pages/agreements/AgreementPage";
import DriverRegister from "./components/pages/drivers/DriverRegister";
import StationSettingsPage from "./components/pages/settings/StationSettingsPage";
import WarehouseRegister from "./components/pages/registers/WarehouseRegister";
import DraftsPage from "./components/pages/drafts/DraftsPage";
import ActiveMissionsPage from "./components/pages/activemissions/ActiveMissionsPage";
import StoragePage from "./components/pages/storage/StoragePage";
import MyMissionsPage from "./components/pages/mymissions/MyMissionsPage";
import MissionTaskRoot from "./components/routers/MissionTaskRoot";
import MyCashSettlementPage from "./components/pages/cashsettlement/MyCashSettlementPage";
import CashSettlementOrdersPage from "./components/pages/cashsettlement/subpages/CashSettlementOrdersPage";
import MyCashSettlementArchivePage from "./components/pages/cashsettlement/subpages/MyCashSettlementArchivePage";
import CashSettlementPage from "./components/pages/cashsettlement/CashSettlementPage";
import { OrderPage } from "./components/pages/order/OrderPage";
import CashSettlementsPage from "./components/pages/cashsettlement/CashSettlementsPage";
import CashOrdersPage from "./components/pages/cashorders/CashOrdersPage";
import VatCodeRegister from "./components/pages/registers/VatCodeRegister";
import ProductGroupRegister from "./components/pages/registers/ProductGroupRegister";
import ExportSettingsPage from "./components/pages/settings/ExportSettingsPage";
import CashExportPage from "./components/pages/cashorders/CashExportPage";
import InvoicingPage from "./components/pages/invoicing/InvoicingPage";
import InvoiceExportPage from "./components/pages/invoicing/InvoiceExportPage";
import OrdersPage from "./components/pages/orders/OrdersPage";
import HomePage from "./components/pages/home/HomePage";
import ActivityLogPage from "./components/pages/log/ActivityLogPage";
import { useStation } from "./providers/StationProvider";
import ActivityLogPane from "./components/pages/log/ActivityLogPane";
import AllMissionsPage from "./components/pages/allmissions/AllMissionsPage";
import { useSignal } from "@emberly/zenith-client";
import OfflinePage from "./components/pages/error/OfflinePage";
import IntegrationsPage from "./components/pages/integrations/IntegrationsPage";
import AllTasksPage from "./components/pages/alltasks/AllTasksPage";
import { EmployeeProvider } from "./providers/EmployeeProvider";
import UserManagementRoot from "./components/pages/usermanagement/common/UserManagementRoot";
import StartLocationRegister from "./components/pages/registers/StartLocationRegister";
import GlobalAdminPage from "./components/pages/globaladmin/GlobalAdminPage";
import CreateStationPage from "./components/pages/globaladmin/CreateStationPage";
import { Intercom } from "@intercom/messenger-js-sdk";
import config from "./config";
import useStaggeredValue from "./hooks/useStaggeredValue";
import ImportPage from "./components/pages/fileimport/ImportPage";
import ProductImportPage from "./components/pages/fileimport/ProductImportPage";
import ContactImportPage from "./components/pages/fileimport/ContactImportPage";
import ConsumptionPage from "./components/pages/globaladmin/ConsumptionPage";
import OperationsMapPage from "./components/pages/operationsmap/OperationsMapPage";
import NavigationPage from "./components/pages/navigation/NavigationPage";
import { useSelector } from "react-redux";
import MissionFormRegister from "./components/pages/missionforms/MissionFormRegister";
import MissionFormPage from "./components/pages/missionforms/MissionFormPage";
import ReceivedMissionRequestsPage from "./components/pages/missionrequests/ReceivedMissionRequestsPage";
import SentMissionRequestsPage from "./components/pages/missionrequests/SentMissionRequestsPage";
import AssistNetworkPage from "./components/pages/assistnetwork/AssistNetworkPage";
import OperatorsPage from "./components/pages/operators/OperatorsPage";
import { ConnectionWidget } from "./components/common/core/ConnectionWidget";

const drawerWidth = "280px";
const compactDrawerWidth = "74px";

// grey 120deg linear-gradient(120deg, #e4e4e4 0%, #cacccd 100%)

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      width: "100%",
      height: "100%",
      position: "absolute",
      background: "rgb(240, 242, 245)", //theme.palette.background.grey
    },
    sidebarMobile: {
      "& .MuiDrawer-paper": {
        width: "80vw",
      }
    },
    sidebarDesktop: {
      "& .MuiDrawer-paper": {
        width: drawerWidth
      },
      flexShrink: 0
    },
    sidebarCompact: {
      "& .MuiDrawer-paper": {
        width: compactDrawerWidth
      },
      "& .MuiListItemIcon-root": {
        marginLeft: "0px" // move the icons back to center
      },
      flexShrink: 0
    },
    contentMobile: {
      width: "100%",
      height: "100%",
    },
    contentDesktop: {
      position: "absolute",
      top: 0,
      left: drawerWidth,
      width: `calc(100% - ${drawerWidth})`,
      height: "100%",
    },
    contentDesktopCompact: {
      position: "absolute",
      top: 0,
      left: compactDrawerWidth,
      width: `calc(100% - ${compactDrawerWidth})`,
      height: "100%",
    },
    contentDesktopLog: {
      position: "absolute",
      top: 0,
      left: drawerWidth,
      width: `calc(100% - ${drawerWidth} - var(--log-width))`,
      height: "100%",
    },
    activityLog: {
      position: "absolute",
      width: `var(--log-width)`,
      height: "100%",
      right: 0,
      top: 0
    },
  })
);

export default function App() {
  const { classes } = useStyles();
  const { isMobilePortraitView, slim, sidebarOpen, setSidebarOpen, isMobile, compactMode, setCompactMode } = useDevice();
  const { showActivityLog, isDriver, user } = useStation();
  const { connecting } = useSignal();
  const [isOffline, setIsOffline] = useState(false);
  const [init, setInit] = useState(!isMobile);
  const navigate = useNavigate();

  const navigationOpen = useSelector(t => t.app.navigationOpen);
  const compactLayoutReady = useStaggeredValue(compactMode, 255);

  useEffect(() => {
    if (connecting && !navigationOpen) {

      const timer = setTimeout(() => setIsOffline(true), 6500);

      return () => {
        clearTimeout(timer);
        setIsOffline(false);
      };
    }
  }, [connecting, navigationOpen]);

  useEffect(() => {
    if (!user) return;
    Intercom({
      app_id: config.intercomId,
      user_id: user.sub,
      name: user.name,
      email: user.email,
      hide_default_launcher: slim,
      custom_attributes: {
        assist_version: config.version
      }
    });
  }, [user, slim]);

  useEffect(() => {
    if (!init) {
      setInit(true);
      const path = window?.location?.pathname;
      if (isMobile && path === "/" && isDriver) {
        navigate("/mymissions", { replace: true });
      }
    }
  }, [isDriver, isMobile, navigate, init]);

  return isOffline || !init ? (
    <OfflinePage />
  ) : (
    <EmployeeProvider>
      <div className={classes.root}>
        <Sidebar
          className={isMobilePortraitView ? classes.sidebarMobile : (compactMode ? classes.sidebarCompact : classes.sidebarDesktop)}
          open={sidebarOpen}
          setOpen={setSidebarOpen}
          compactLayoutReady={compactLayoutReady}
          compactMode={compactMode}
          setCompactMode={setCompactMode}
        />
        <Box className={slim ? classes.contentMobile : (compactLayoutReady ? classes.contentDesktopCompact : (showActivityLog ? classes.contentDesktopLog : classes.contentDesktop))}>
          <Suspense>
            <Routes>

              <Route path="/" element={<HomePage />} />
              <Route path="/log" element={<ActivityLogPage />} />

              <Route path="/dispatch" element={<DispatchPage />} />
              <Route path="/dispatch/:missionId" element={<MissionPage />} />
              <Route path="/dispatch/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/navigation" element={<NavigationPage />} />

              <Route path="/map" element={<OperationsMapPage />} />
              <Route path="/map/:missionId" element={<MissionPage />} />
              <Route path="/map/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/drafts" element={<DraftsPage />} />
              <Route path="/drafts/:missionId" element={<MissionPage />} />
              <Route path="/drafts/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/activemissions" element={<ActiveMissionsPage />} />
              <Route path="/activemissions/:missionId" element={<MissionPage />} />
              <Route path="/activemissions/:missionId/:taskNumber" element={<MissionPage />} />


              <Route path="/missions" element={<AllMissionsPage />} />
              <Route path="/missions/:missionId" element={<MissionPage />} />
              <Route path="/missions/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/tasks" element={<AllTasksPage />} />
              <Route path="/tasks/:missionId/" element={<MissionPage />} />
              <Route path="/tasks/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/storage" element={<StoragePage />} />
              <Route path="/storage/:missionId" element={<MissionPage />} />
              <Route path="/storage/:missionId/:taskNumber" element={<MissionPage />} />

              <Route path="/causes" element={<CauseRegister />} />
              <Route path="/warehouses" element={<WarehouseRegister />} />
              <Route path="/startlocations" element={<StartLocationRegister />} />
              <Route path="/vatcodes" element={<VatCodeRegister />} />
              <Route path="/productgroups" element={<ProductGroupRegister />} />
              <Route path="/vehicles" element={<ServiceVehicleRegister />} />
              <Route path="/tags" element={<MissionTagRegister />} />
              <Route path="/products" element={<ProductRegister />} />


              <Route path="/contacts" element={<ContactRegister />} />
              <Route path="/contacts/:tabId" element={<ContactRegister />} />

              <Route path="/contacts/:tabId/:contactId/:tabId" element={<ContactPage />} />
              <Route path="/contacts/:tabId/:contactId/missions/:missionId" element={<MissionPage />} />
              <Route path="/contacts/:tabId/:contactId/orders/:missionId/" element={<MissionPage />} />



              <Route path="/contacts/:tabId/:contactId/:tabId" element={<ContactPage />} />
              <Route path="/contacts/:tabId/:contactId/missions/:missionId" element={<MissionPage />} />
              <Route path="/contacts/:tabId/:contactId/orders/:missionId/" element={<MissionPage />} />


              <Route path="/agreements" element={<AgreementRegister />} />
              <Route path="/agreements/:agreementId" element={<AgreementPage />} />
              <Route path="/drivers" element={<DriverRegister />} />
              <Route path="/operators" element={<OperatorsPage />} />

              <Route path="/mymissions" element={<MyMissionsPage />} />
              <Route path="/mymissions/:missionId/:taskId/*" element={<MissionTaskRoot />} />

              <Route path="/mycashsettlement" element={<MyCashSettlementPage />} />
              <Route path="/mycashsettlement/orders" element={<CashSettlementOrdersPage />} />
              <Route path="/mycashsettlement/orders/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/mycashsettlement/orders/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/mycashsettlement/orders/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />


              <Route path="/mycashsettlement/archive" element={<MyCashSettlementArchivePage />} />
              <Route path="/mycashsettlement/archive/:settlementId" element={<CashSettlementPage />} />
              <Route path="/mycashsettlement/archive/:settlementId/orders" element={<CashSettlementOrdersPage />} />
              <Route path="/mycashsettlement/archive/:settlementId/orders/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/mycashsettlement/archive/:settlementId/orders/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/mycashsettlement/archive/:settlementId/orders/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />


              <Route path="/cashsettlements" element={<CashSettlementsPage />} />
              <Route path="/cashsettlements/:tabId" element={<CashSettlementsPage />} />
              <Route path="/cashsettlements/:tabId/:settlementId" element={<CashSettlementPage />} />
              <Route path="/cashsettlements/:tabId/:settlementId/orders" element={<CashSettlementOrdersPage />} />
              <Route path="/cashsettlements/:tabId/:settlementId/orders/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashsettlements/:tabId/:settlementId/orders/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashsettlements/:tabId/:settlementId/orders/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />
              <Route path="/cashsettlements/unsettled/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashsettlements/unsettled/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashsettlements/unsettled/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />





              <Route path="/cashorders" element={<CashOrdersPage />} />
              <Route path="/cashorders/:tabId" element={<CashOrdersPage />} />
              <Route path="/cashorders/requested/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashorders/requested/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashorders/requested/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/cashorders/ready/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashorders/ready/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashorders/ready/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/cashorders/journals/:exportId" element={<CashExportPage />} />
              <Route path="/cashorders/journals/:exportId/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashorders/journals/:exportId/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashorders/journals/:exportId/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />


              <Route path="/cashorders/journalentries/:exportId" element={<CashExportPage />} />
              <Route path="/cashorders/journalentries/:exportId/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/cashorders/journalentries/:exportId/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/cashorders/journalentries/:exportId/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/invoicing" element={<InvoicingPage />} />
              <Route path="/invoicing/:tabId" element={<InvoicingPage />} />

              <Route path="/invoicing/open/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/invoicing/open/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/invoicing/open/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/invoicing/ready/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/invoicing/ready/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/invoicing/ready/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/invoicing/journals/:exportId" element={<InvoiceExportPage />} />
              <Route path="/invoicing/journals/:exportId/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/invoicing/journals/:exportId/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/invoicing/journals/:exportId/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />

              <Route path="/invoicing/journalentries/:exportId" element={<InvoiceExportPage />} />
              <Route path="/invoicing/journalentries/:exportId/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/invoicing/journalentries/:exportId/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/invoicing/journalentries/:exportId/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />


              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:tabId" element={<OrdersPage />} />
              <Route path="/orders/:tabId/:missionId/:orderId" element={<OrderPage />} />
              <Route path="/orders/:tabId/:missionId/:orderId/mission" element={<MissionPage />} />
              <Route path="/orders/:tabId/:missionId/:orderId/mission/:taskNumber" element={<MissionPage />} />


              <Route path="/station/settings" element={<StationSettingsPage />} />
              <Route path="/station/export" element={<ExportSettingsPage />} />
              <Route path="/station/import" element={<ImportPage />} />
              <Route path="/station/import/products" element={<ProductImportPage />} />
              <Route path="/station/import/contacts" element={<ContactImportPage />} />



              <Route path="/network/requests/received/" element={<ReceivedMissionRequestsPage />} />
              <Route path="/network/requests/received/:tabId" element={<ReceivedMissionRequestsPage />} />
              <Route path="/network/requests/received/:tabId/:missionId" element={<MissionPage />} />
              <Route path="/network/requests/received/:tabId/:missionId/:taskNumber" element={<MissionPage />} />


              <Route path="/network/requests/sent/" element={<SentMissionRequestsPage />} />
              <Route path="/network/requests/sent/:tabId" element={<SentMissionRequestsPage />} />
              <Route path="/network/requests/sent/:tabId/:missionId" element={<MissionPage />} />
              <Route path="/network/requests/sent/:tabId/:missionId/:taskNumber" element={<MissionPage />} />



              <Route path="/network/missionforms" element={<MissionFormRegister />} />
              <Route path="/network/missionforms/:formId" element={<MissionFormPage />} />

              <Route path="/network/assist" element={<AssistNetworkPage />} />


              <Route path="/station/integrations" element={<IntegrationsPage />} />
              <Route path="/station/users" element={<UserManagementRoot />} />
              <Route path="/station/globaladmin" element={<GlobalAdminPage />} />
              <Route path="/station/globaladmin/createstation" element={<CreateStationPage />} />
              <Route path="/station/globaladmin/consumption" element={<ConsumptionPage />} />

              <Route path="*" element={<NotFoundPage setSidebarOpen={setSidebarOpen} />} />

            </Routes>
          </Suspense>
        </Box>
        {showActivityLog && !compactMode ? (
          <Box className={classes.activityLog}>
            <ActivityLogPane />
          </Box>
        ) : null}
        <ConnectionWidget />
      </div>
    </EmployeeProvider>
  );
}